import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, User, Video, FileText, Heart, Activity, Plus } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [stats, setStats] = useState({
    totalAppointments: 0,
    completedAppointments: 0,
    upcomingAppointments: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/appointments/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUpcomingAppointments(data.upcoming || []);
        setRecentAppointments(data.recent || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time;
  };

  const backgroundImageUrl = `${API_BASE_URL}/uploads/backgrounds/dashboard.jpg`;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Preload image to check if it's accessible
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
      setImageError(false);
      console.log('Dashboard background image loaded successfully:', backgroundImageUrl);
    };
    img.onerror = () => {
      setImageError(true);
      setImageLoaded(false);
      console.error('Failed to load dashboard background image:', backgroundImageUrl);
    };
    img.src = backgroundImageUrl;
  }, [backgroundImageUrl]);

  // Fallback background color
  const backgroundStyle = imageError 
    ? { backgroundColor: '#3b82f6', minHeight: '300px' }
    : {
        backgroundImage: imageLoaded 
          ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${backgroundImageUrl})`
          : 'linear-gradient(rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.6))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '300px'
      };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background */}
      <div 
        className="relative py-20 mb-8"
        style={backgroundStyle}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Welcome back, {user?.name}!
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Here's what's happening with your health today
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Link
            to="/appointments/new"
            className="card hover:shadow-lg transition-shadow duration-200 text-center group"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mb-4 group-hover:bg-primary-200 transition-colors duration-200">
              <Plus className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Book Appointment</h3>
            <p className="text-gray-600 text-sm">Schedule a new consultation</p>
          </Link>

          <Link
            to="/doctors"
            className="card hover:shadow-lg transition-shadow duration-200 text-center group"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary-100 rounded-lg mb-4 group-hover:bg-secondary-200 transition-colors duration-200">
              <User className="h-6 w-6 text-secondary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Find Doctors</h3>
            <p className="text-gray-600 text-sm">Browse healthcare providers</p>
          </Link>

          <Link
            to="/profile"
            className="card hover:shadow-lg transition-shadow duration-200 text-center group"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4 group-hover:bg-purple-200 transition-colors duration-200">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Health Records</h3>
            <p className="text-gray-600 text-sm">View your medical history</p>
          </Link>

          <Link
            to="/appointments"
            className="card hover:shadow-lg transition-shadow duration-200 text-center group"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mb-4 group-hover:bg-orange-200 transition-colors duration-200">
              <Calendar className="h-6 w-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">All Appointments</h3>
            <p className="text-gray-600 text-sm">Manage your schedule</p>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Appointments</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalAppointments}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                <Heart className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.completedAppointments}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Upcoming</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.upcomingAppointments}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upcoming Appointments */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Upcoming Appointments</h2>
              <Link to="/appointments" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
            
            {upcomingAppointments.length > 0 ? (
              <div className="space-y-4">
                {upcomingAppointments.slice(0, 3).map((appointment) => (
                  <div key={appointment._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="inline-flex items-center justify-center w-10 h-10 bg-primary-100 rounded-full">
                        <User className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {user?.userType === 'patient' ? appointment.doctor.name : appointment.patient.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatDate(appointment.appointmentDate)} at {formatTime(appointment.startTime)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(appointment.status)}`}>
                        {appointment.status}
                      </span>
                      {appointment.mode === 'video' && (
                        <Link
                          to={`/video-call/${appointment._id}`}
                          className="inline-flex items-center justify-center w-8 h-8 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors duration-200"
                        >
                          <Video className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No upcoming appointments</p>
                <Link to="/appointments/new" className="text-primary-600 hover:text-primary-700 font-medium">
                  Book your first appointment
                </Link>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
              <Link to="/appointments" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
            
            {recentAppointments.length > 0 ? (
              <div className="space-y-4">
                {recentAppointments.slice(0, 3).map((appointment) => (
                  <div key={appointment._id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full">
                      <Activity className="h-4 w-4 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {appointment.type} appointment
                      </p>
                      <p className="text-xs text-gray-600">
                        {formatDate(appointment.appointmentDate)} • {appointment.status}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(appointment.status)}`}>
                      {appointment.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Health Tips */}
        <div className="card mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Health Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">Stay Hydrated</h3>
              <p className="text-blue-700 text-sm">Drink at least 8 glasses of water daily for optimal health.</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="font-medium text-green-900 mb-2">Regular Exercise</h3>
              <p className="text-green-700 text-sm">Aim for 150 minutes of moderate exercise per week.</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h3 className="font-medium text-purple-900 mb-2">Quality Sleep</h3>
              <p className="text-purple-700 text-sm">Get 7-9 hours of sleep for better physical and mental health.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
